import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { format, differenceInDays } from "date-fns";

function Events() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);

  const SHEET_URL =
    "https://docs.google.com/spreadsheets/d/1RCT6-OndGM-VZ7XRNsjNWCW0eL8pXUudgp-kQ11ynr0/gviz/tq?tqx=out:json&sheet=MasjidAboTherEventSheet";

  function parseGoogleDate(str) {
    if (!str) return null;
    if (str instanceof Date) return str;

    if (typeof str === "string" && str.startsWith("Date(")) {
      try {
        const parts = str
          .replace("Date(", "")
          .replace(")", "")
          .split(",")
          .map((x) => parseInt(x.trim(), 10));

        const [year, month, day, hour = 0, minute = 0, second = 0] = parts;
        return new Date(year, month, day, hour, minute, second);
      } catch {
        return null;
      }
    }

    const d = new Date(str);
    return isNaN(d) ? null : d;
  }

  useEffect(() => {
    async function fetchEvents() {
      try {
        const res = await fetch(SHEET_URL);
        const text = await res.text();
        console.log("Sheet response:", text);

        const json = JSON.parse(text.substr(47).slice(0, -2));

        const rows = json.table.rows.map((r) => {
          const date = parseGoogleDate(r.c[0]?.v);
          const time = parseGoogleDate(r.c[2]?.v);

          const timeFormatted = time
            ? time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
            : "";

          return {
            date,
            title: r.c[1]?.v || "",
            time: timeFormatted,
            description: r.c[3]?.v || "",
          };
        });

        // Fetch Zuhr (Dhuhr) times for Fridays from Aladhan and merge
        async function fetchZuhrFridayEvents() {
          try {
            const city = "Ottawa";
            const country = "Canada";
            const method = 2; // ISNA

            const now = new Date();
            
            // Collect months that contain the next 28 days
            const monthsToFetch = new Set();
            for (let i = 0; i < 28; i++) {
              const d = new Date(now);
              d.setDate(d.getDate() + i);
              const key = `${d.getMonth() + 1}-${d.getFullYear()}`;
              monthsToFetch.add(key);
            }

            const zuhrEvents = [];

            for (const monthKey of monthsToFetch) {
              const [monthStr, yearStr] = monthKey.split("-");
              const month = parseInt(monthStr, 10);
              const year = parseInt(yearStr, 10);

              const url = `https://api.aladhan.com/v1/calendarByCity?city=${encodeURIComponent(
                city
              )}&country=${encodeURIComponent(country)}&method=${method}&month=${month}&year=${year}`;

              const r = await fetch(url);
              const data = await r.json();
              if (!data || !data.data) continue;

              data.data.forEach((dayEntry) => {
                // Aladhan returns gregorian.date in format 'DD-MM-YYYY'
                const greg = dayEntry.date?.gregorian?.date;
                if (!greg) return;
                const [dd, mm, yyyy] = greg.split("-").map((v) => parseInt(v, 10));

                // timings.Dhuhr may include timezone in parentheses, strip it
                let dhuhrRaw = dayEntry.timings?.Dhuhr || dayEntry.timings?.Zuhur || "";
                dhuhrRaw = dhuhrRaw.replace(/\s*\(.+\)/, "").trim();

                const [hourStr, minuteStr] = dhuhrRaw.split(":");
                if (!hourStr || !minuteStr) return;

                const dateObj = new Date(yyyy, mm - 1, dd, parseInt(hourStr, 10), parseInt(minuteStr, 10));

                // Only include Fridays within the next 28 days
                const diffDays = Math.floor((dateObj - now) / (1000 * 60 * 60 * 24));
                if (diffDays < 0 || diffDays > 27) return;

                // JavaScript: 5 === Friday
                if (dateObj.getDay() === 5) {
                  zuhrEvents.push({
                    date: dateObj,
                    title: "Jumu'ah (Friday) Prayer",
                    time: `${hourStr.padStart(2, "0")}:${minuteStr.padStart(2, "0")}`,
                    description: "Join us for the weekly congregational Friday prayer.",
                  });
                }
              });
            }

            return zuhrEvents;
          } catch (e) {
            console.error("Error fetching Zuhr times from Aladhan:", e);
            return [];
          }
        }

        const zuhr = await fetchZuhrFridayEvents();

        // Merge Google Sheet rows and Zuhr events, avoiding duplicates by date+title
        const all = [...rows, ...zuhr];
        const unique = [];
        const seen = new Set();

        all.forEach((ev) => {
          if (!ev || !ev.date) return;
          const key = `${format(ev.date, "yyyy-MM-dd")}|${ev.title}`;
          if (!seen.has(key)) {
            seen.add(key);
            unique.push(ev);
          }
        });

        setEvents(unique.filter((e) => e.date instanceof Date && !isNaN(e.date)));
      } catch (err) {
        console.error("Error loading Google Sheet data:", err);
      }
    }

    fetchEvents();
    const interval = setInterval(fetchEvents, 60000 * 5);
    return () => clearInterval(interval);
  }, []);

  const eventsForSelectedDate = events.filter(
    (event) =>
      event.date &&
      !isNaN(event.date) &&
      format(event.date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd")
  );

  const now = new Date();

  return (
    <div style={styles.page}>
      <h1 style={styles.heading}>Mosque Events Calendar</h1>
      <p style={styles.subtitle}>Tap a date to view events</p>

      {/* Calendar */}
      <div style={styles.calendarWrapper}>
        <Calendar
          onChange={setSelectedDate}
          value={selectedDate}
         tileContent={({ date }) => {
        const eventsOnDate = events.filter(
          (e) =>
            format(e.date, "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
        );

        if (eventsOnDate.length === 0) return null;

        const now = new Date();
        let hasFuture = false;
        let hasPast7 = false;

        eventsOnDate.forEach((event) => {
          if (event.date > now) {
            hasFuture = true;
          } else {
            const diffMs = now - event.date;
            const diffDays = diffMs / (1000 * 60 * 60 * 24);

            if (diffDays <= 7) {
              hasPast7 = true;
            }
          }
        });

        if (hasFuture) {
          return <div style={{ ...styles.eventDot, backgroundColor: "#067446" }} />;
        }

        if (hasPast7) {
          return <div style={{ ...styles.eventDot, backgroundColor: "#999" }} />;
        }

        return null;
      }}

          className="modern-calendar"
        />
      </div>

    {/* Events List */}
    <div style={styles.eventList}>
      {eventsForSelectedDate.length > 0 ? (
        eventsForSelectedDate.map((event, i) => (
          <div key={i} style={styles.eventCard}>
            <div style={styles.eventHeader}>
              <span style={styles.eventDate}>
                {format(event.date, "EEEE, MMMM d, yyyy")}
              </span>
            </div>

            <h3 style={styles.eventTitle}>{event.title}</h3>

            <div style={styles.eventInfoRow}>
              <span style={styles.eventTime}>
                <strong>🕒</strong> {event.time || "N/A"}
              </span>
              <span style={styles.separator}> | </span>
              <span style={styles.eventDesc}>{event.description}</span>
            </div>
          </div>
        ))
      ) : (
        <p style={styles.noEvents}>No events scheduled for this day.</p>
      )}
    </div>

      <style>
        {`
          .modern-calendar {
            border: none !important;
            box-shadow: 0 4px 15px rgba(0,0,0,0.08);
            border-radius: 16px;
            padding: 10px;
            background: #ffffff;
            font-family: 'Georgia', serif;
          }

          .react-calendar__tile--active {
            background: #067446 !important;
            color: white !important;
            border-radius: 8px;
          }

          .react-calendar__tile--now {
            background: #d19f14 !important;
            color: white !important;
            border-radius: 8px;
          }

          .react-calendar__tile:hover {
            background: #e8f5f1 !important;
            border-radius: 8px;
          }
        `}
      </style>
    </div>
  );
}

const styles = {
  page: {
    backgroundColor: "#fff",
    fontFamily: "'Georgia', serif",
    padding: "0px 25px",
    textAlign: "center",
    color: "#333",
  },
  heading: {
    color: "#067446",
    fontSize: "2.2rem",
    marginBottom: "10px",
    fontWeight: "400",
  },
  subtitle: {
    color: "#d19f14",
    fontSize: "1rem",
    marginBottom: "25px",
    fontStyle: "italic",
  },
  calendarWrapper: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "40px",
  },
  eventDot: {
    height: "8px",
    width: "8px",
    borderRadius: "50%",
    margin: "0 auto",
    marginTop: "3px",
  },
  eventList: {
    maxWidth: "700px",
    margin: "0 auto",
    backgroundColor: "#f9f9f9",
    borderRadius: "16px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
    padding: "30px",
  },
  eventCard: {
    backgroundColor: "#ffffff",
    padding: "20px 25px",
    borderLeft: "6px solid #d19f14",
    borderRadius: "12px",
    marginBottom: "20px",
    textAlign: "left",
    boxShadow: "0 3px 10px rgba(0,0,0,0.05)",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  eventHeader: {
    marginBottom: "8px",
  },
  eventDate: {
    color: "#d19f14",
    fontWeight: "bold",
    fontSize: "1rem",
  },
  eventContent: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  eventTitle: {
    color: "#067446",
    fontSize: "1.3rem",
    margin: 0,
  },
  eventDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  eventTime: {
    color: "#333",
    fontWeight: "500",
    margin: 0,
  },
  eventDesc: {
    color: "#555",
    margin: 0,
  },
  noEvents: {
    color: "#888",
    fontStyle: "italic",
    marginTop: "10px",
  },
};

export default Events;
